import { useMemo, useState, useEffect } from "react";
import {
	Box,
	IconButton,
	Tooltip,
	Typography,
	CircularProgress,
	Badge,
} from "@material-ui/core";
import { useProjectsOT } from "src/hooks/redux/useProjectsOT";
import { PercentageCell } from "../Misc/PercentageCell";
import { ChevronRight, Edit, Trash2 } from "react-feather";
import { NeoPlaceAssignDialog } from "./Dialog/NeoPlaceAssign";
import { useHistory } from "react-router-dom";
import { getRef } from "src/utils/firebase";
import { DropdownMenu } from "../../Generic/DropdownMenu";
import { Opener } from "../../CheckPro/Opener";
import { colorsCheckPro } from "src/theme/variants";
import naturalSort from "natural-sort";
import { NeoPlaceDisableDialog } from "./Dialog/NeoPlaceDisable";
import { AssignedPlaceOT } from "src/models/OT/Place";
import { Table } from "src/utils/components/Table";
import { getSignaturesSummaryPdf } from 'src/redux/neo-checklists/actions/reports';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from "src/redux/store";
import {getPlaceSheetsOT, getChecklistPlacesWithYellowResponses} from "../../../redux/neo-checklists/actions/sheets";
import {PictureAsPdfTwoTone, Warning} from "@material-ui/icons";
import { hasYellowResponses, hasBlueYellowResponses } from "src/models/OT/utils/sheets";
import { getUsers } from "../../../redux/actions/usersActions";

export const NeoPlacesTable = () => {
	const history = useHistory();
	// @ts-ignore
	const dispatch = useDispatch<AppDispatch>();
	const user = useSelector((s) => s.authReducer.user);
	const users = useSelector((s) => s.usersReducer.users);

	const [dialog, setDialog] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [placesWithYellowResponses, setPlacesWithYellowResponses] = useState<Record<string, boolean>>({});
	const [placesWithBlueYellowResponses, setPlacesWithBlueYellowResponses] = useState<Record<string, boolean>>({});
	const [placesWithInProcessResponses, setPlacesWithInProcessResponses] = useState<Record<string, boolean>>({});

	// Load users data on component mount
	useEffect(() => {
		dispatch(getUsers());
	}, [dispatch]);



	const { selected, checklist, set, toggle } =
		useProjectsOT();

	const dataToShow = useMemo(() => {
		if (!checklist.places) {
			console.log('📋 [DataToShow] No hay checklist.places');
			return [];
		}
		const filtered = checklist.places
			.filter((p) => p.Asignado)
			.sort((a, b) => naturalSort()(a.Nombre, b.Nombre));

		console.log('📋 [DataToShow] Espacios procesados:', {
			totalPlaces: checklist.places.length,
			assignedPlaces: filtered.length,
			places: filtered.map(p => ({ id: getRef(p).id, name: p.Nombre }))
		});

		return filtered;
	}, [checklist.places]);

	// Check for yellow and blue-yellow responses in each place
	useEffect(() => {
		if (!dataToShow.length || !selected.checklist) return;

		const checkPlacesForResponses = async () => {
			try {
				// No need to set loading state since we're using already loaded data
				// setIsCheckingResponses(true);

				// Get the current checklist ID
				const checklistId = getRef(selected.checklist).id;

				// Always fetch data for in-process responses
				let shouldFetchData = true;

				// Check if we already have data in localStorage for yellow and blue-yellow responses
				const storedYellowData = localStorage.getItem('placesWithYellowResponses');
				const storedBlueYellowData = localStorage.getItem('placesWithBlueYellowResponses');

				// Try to use localStorage data for yellow and blue-yellow responses
				if (storedYellowData && storedBlueYellowData && shouldFetchData) {
					try {
						const yellowResponsesData = JSON.parse(storedYellowData);
						const blueYellowResponsesData = JSON.parse(storedBlueYellowData);

						// If we have data for this checklist, use it for yellow and blue-yellow responses
						if (yellowResponsesData[checklistId] && blueYellowResponsesData[checklistId]) {
							const yellowResults: Record<string, boolean> = {};
							const blueYellowResults: Record<string, boolean> = {};

							// Map the localStorage data to our state
							dataToShow.forEach(place => {
								const placeId = getRef(place).id;
								yellowResults[placeId] = !!yellowResponsesData[checklistId][placeId];
								blueYellowResults[placeId] = !!blueYellowResponsesData[checklistId][placeId];
							});

							setPlacesWithYellowResponses(yellowResults);
							setPlacesWithBlueYellowResponses(blueYellowResults);
							// We still need to fetch data for in-process responses
						}
					} catch (parseError) {
						// If localStorage data is invalid, clear it
						console.error("Invalid localStorage data:", parseError);
						localStorage.removeItem('placesWithYellowResponses');
						localStorage.removeItem('placesWithBlueYellowResponses');
						// We'll need to fetch all data
					}
				}

				// Process in-process responses using pre-calculated hasPendingItems field from already loaded data
				const inProcessResults: Record<string, boolean> = {};

				dataToShow.forEach(place => {
					const placeId = getRef(place).id;

					// Use pre-calculated hasPendingItems field directly from the already loaded place data
					const hasInProcessResponses = (place as any).hasPendingItems || false;

					console.log(`[P Calculation] Place ${placeId} (${place.Nombre}) - hasPendingItems:`, (place as any).hasPendingItems, '(type:', typeof (place as any).hasPendingItems, ') -> hasInProcessResponses:', hasInProcessResponses);

					// If hasPendingItems is undefined, it means the field hasn't been calculated yet
					if ((place as any).hasPendingItems === undefined) {
						console.warn(`[P Calculation] Place ${placeId} (${place.Nombre}) - hasPendingItems field is undefined. Field needs to be populated by system.`);
					}

					inProcessResults[placeId] = hasInProcessResponses;
				});

				setPlacesWithInProcessResponses(inProcessResults);

				// Only fetch data if we don't have it in localStorage for yellow/blue-yellow responses
				if (shouldFetchData) {
					// Use the optimized action to get yellow and blue-yellow responses only
					try {
						const result = await dispatch(getChecklistPlacesWithYellowResponses()).unwrap();
						// Update state with yellow and blue-yellow results (in-process already handled above)
						setPlacesWithYellowResponses(result.yellowResponses);
						setPlacesWithBlueYellowResponses(result.blueYellowResponses);
					} catch (actionError) {
						console.error("Error fetching yellow/blue-yellow responses:", actionError);
					}
				}
			} catch (error) {
				console.error("Error checking responses:", error);
			}
		};

		checkPlacesForResponses();
	}, [dataToShow, dispatch, selected.checklist]);



	const calculateProgress = (ofPlace: AssignedPlaceOT) => {
		const { Cantidad, CantidadCompletada = 0 } = ofPlace;

		const percentage =
			(CantidadCompletada / Cantidad) * 100;

		if (isNaN(percentage)) return 0;
		return percentage;
	};

	const handleEdit = (place: AssignedPlaceOT) => {
		toggle.assignedPlace([place]);
		setDialog(1);
	};

	const handleDelete = (place: AssignedPlaceOT) => {
		toggle.assignedPlace([place]);
		setDialog(2);
	};

	const handleRedirectToAnswers = (
		ofPlace: AssignedPlaceOT
	) => {
		const placeRef = getRef(ofPlace);
		const checklistRef = getRef(selected.checklist);

		set.assignedPlace(ofPlace);
		history.push(`${checklistRef.id}/${placeRef.id}`);
	};

	const handleDownloadResponses = async (place: AssignedPlaceOT) => {
		try {
			console.log(place)
			setIsLoading(true);
			set.assignedPlace(place);
			const sheets = await dispatch(getPlaceSheetsOT()).unwrap();

			if (!sheets) {
				console.log("No hay respuestas para descargar");
				return;
			}

			const zip = new JSZip();

			for (const sheet of sheets) {
				try {
					const pdfBlob = await dispatch(getSignaturesSummaryPdf({
						responsePath: getRef(sheet).path,
						// @ts-ignore
						responseNumber: sheet._position,
					})).unwrap();

					if (pdfBlob instanceof Blob) {
						zip.file(`${sheet.NombreChecklist}_${sheet.NombreEspacio}_${sheet._position}.pdf`, pdfBlob);
					} else {
						console.error(`El PDF para la hoja ${sheet._position} no es un Blob válido`);
					}
				} catch (error) {
					console.error(`Error al obtener el PDF para la hoja ${sheet._position}:`, error);
				}
			}

			if (zip.files && Object.keys(zip.files).length > 0) {
				const zipBlob = await zip.generateAsync({type: "blob"});
				saveAs(zipBlob, `${place.Nombre}_respuestas.zip`);
				console.log("Respuestas descargadas exitosamente");
			} else {
				console.log("No se pudo generar ningún PDF para descargar");
			}
		} catch (error) {
			console.error("Error al descargar respuestas:", error);
		} finally {
			setIsLoading(false);
		}
	};
	return (
		<>
			<Table
				stripped
				elementsProps={{
					TableCellProps: { style: { padding: 5 } },
				}}
				disableExporting
				loading={!checklist.places}
				list={dataToShow}
				emptyMessage="Debe asignar espacios a la lista de chequeo"
				columns={[
					{
						header: "",
						width: 40,
						align: "center",
						Cell: ({ value }) => {
							const placeId = getRef(value).id;
							const hasInProcessResponse = placesWithInProcessResponses[placeId];

							// Mostrar óvalo directamente cuando hay respuestas pendientes (sin estado de carga)
							if (hasInProcessResponse) {
								const tooltipTitle = "Este espacio tiene respuestas en proceso";

								return (
									<Tooltip title={tooltipTitle}>
										<Box
											display="flex"
											alignItems="center"
											justifyContent="center"
											width={16}
											height={16}
											borderRadius="50%"
											bgcolor="#FFFF00"
											border="1px solid rgba(0, 0, 0, 0.5)"
											style={{ margin: '0 auto', position: 'relative' }}
										>
											<Typography
												variant="body2"
												style={{
													color: 'rgba(0, 0, 0, 0.5)',
													fontWeight: 'bold',
													fontSize: 10,
													lineHeight: 1
												}}
											>
												P
											</Typography>
										</Box>
									</Tooltip>
								);
							}

							return null;
						},
					},
					{
						header: "Nombre",
						Cell: ({ value }) => {
							return (
								<Box display="flex" alignItems="center">
									{value.Nombre}
								</Box>
							);
						},
					},
					{
						header: "Responsable",
						Cell: ({ value }) => {
							// Obtener el primer revisor (Revisor 1) directamente del espacio asignado
							const revisor1Ref = value.Revisores && value.Revisores.length > 0 ? value.Revisores[0] : null;

							if (!revisor1Ref) {
								return <>Sin asignar</>;
							}

							// Buscar los datos del usuario en la lista de usuarios
							const revisor1User = users?.find(u => u._ref?.id === revisor1Ref.id);

							if (revisor1User) {
								return <>{revisor1User.Nombre} {revisor1User.Apellido}</>;
							}

							return <>Cargando...</>;
						},
					},
					{
						align: "center",
						header: "Cantidad",
						Cell: ({ value }) => (
							<>
								{value.CantidadCompletada ?? 0} /{" "}
								{value.Cantidad}
							</>
						),
					},
					{
						align: "center",
						header: "Unidad",
						Cell: () => (
							<>{selected.checklist?.Unidad ?? "Unidad"}</>
						),
					},
					{
						align: "center",
						header: "Progreso",
						Cell: ({ value }) => (
							<Tooltip
								title={
									<Typography>
										<ul>
											<li>Menor o igual del 25%: rojo </li>
											<li>
												Sobre 25% y menos 50%: naranjo
											</li>
											<li>
												Sobre 50% y menos al 75%: amarillo
											</li>
											<li>Sobre 75% y menos a 99%: azul</li>
											<li>Sobre el 99%: verde</li>
										</ul>
										Si la meta es 50%, y has completado un
										51%, se vería en verde porque superaría
										el 99% RESPECTO A LA META.
									</Typography>
								}
							>
								<Box>
									<PercentageCell
										value={calculateProgress(value)}
										aim={selected.checklist?.MetaRevision}
									/>
								</Box>
							</Tooltip>
						),
					},
					{
						align: "right",
						Cell: ({ value }) => (
							<>
								{user?.tipoUsuario !== "UsuarioEmpresa" && !selected.project?.Eliminado && (
										<DropdownMenu
											Opener={Opener}
											options={[
												{
													label: "Editar",
													onClick: () => handleEdit(value),
													icon: (
														<Edit
															color={colorsCheckPro.primary}
														/>
													),
												},
												{
													label: "Eliminar",
													onClick: () =>
														handleDelete(value),
													icon: <Trash2 color="red" />,
												},
												{
													label: "Respuestas y firmas",
													icon:
														<PictureAsPdfTwoTone
															htmlColor={  "red" }
														/>,
													onClick: () => handleDownloadResponses(value),
												},
											]}
										/>
									)}

								{user?.tipoUsuario === "UsuarioEmpresa" && !selected.project?.Eliminado && (
									<DropdownMenu
										Opener={Opener}
										options={[
											{
												label: "Respuestas y firmas",
												icon:
													<PictureAsPdfTwoTone
														htmlColor={  "red" }
													/>,
												onClick: () => handleDownloadResponses(value),
											},
										]}
									/>
								)}

								<Tooltip title="Ver respuestas">
									<IconButton
										size="small"
										color="primary"
										onClick={() =>
											handleRedirectToAnswers(value)
										}
									>
										<ChevronRight />
									</IconButton>
								</Tooltip>
							</>
						),
					},
				]}
			/>

			<NeoPlaceAssignDialog
				mode="edit"
				open={dialog === 1}
				onClose={() => setDialog(0)}
			/>

			<NeoPlaceDisableDialog
				open={dialog === 2}
				onClose={() => setDialog(0)}
			/>

			{isLoading && (
				<Box
					position="fixed"
					top={0}
					left={0}
					width="100%"
					height="100%"
					display="flex"
					alignItems="center"
					justifyContent="center"
					bgcolor="rgba(0, 0, 0, 0.5)"
					zIndex={9999}
				>
					<CircularProgress />
				</Box>
			)}
		</>
	);
};
